import _ from 'underscore'

import api from 'src/store/api'
import dates from 'src/utils/dates'
import http from 'src/store/http'
import transform from 'src/utils/transform'

import actionNames from './actionNames'
import { fireanalytics } from '@/store/firebase'
import PATHS from '@/router/paths'

const actions = {
  async getData({ commit, dispatch, rootGetters, rootState }, reportKey) {
    const hasRightsToAdjuster = rootGetters['auth/isUserInternalDev']
    const { currentReportParams } = rootState.tools
    const root = { root: true }

    if (currentReportParams.adjuster) {
      if (!hasRightsToAdjuster) {
        currentReportParams.adjuster = false
        dispatch('messages/showLoadingMessage', { reportKey, text: 'Requesting data ...' }, { root: true })
      } else {
        dispatch('messages/showLoadingMessage', { reportKey, text: 'Requesting Adjuster data ...' }, { root: true })
      }
    } else {
      dispatch('messages/showLoadingMessage', { reportKey, text: 'Requesting data ...' }, { root: true })
    }

    commit('tools/GET_DATA_REFRESHED_TIME_REQUESTED', true, root)

    return dispatch('getDeliveryData', reportKey)
  },

  openTab(context, url) {
    window.open(url, '_blank')
  },
  openTabToLineItem({ dispatch }, item) {
    const url = item.lineItemGamLink
    dispatch('openTab', url)
  },
  openTabToOrder({ dispatch }, item) {
    const url = item.orderGamLink
    dispatch('openTab', url)
  },

  async fetchLineItems({ commit, dispatch }, { apiParams, reportKey }) {
    const root = { root: true }

    dispatch('messages/showLoadingMessage', { reportKey, text: 'Requesting line items ...' }, { root: true })

    const lineItemRequest = api.getLineItemsForPublisher(apiParams)

    const catcher = (err) => {
      const error = http.catch(err)

      dispatch('messages/showErrorMessage', { error, reportKey }, { root: true })
      commit('tools/GET_DATA_REFRESHED_TIME_FAILED', error, root)

      return error
    }

    return lineItemRequest.catch(catcher)
  },
  async pingEndpoint({ dispatch }, { apiParams, catcher, dispatchName, reportKey }) {
    const pingParams = { ...apiParams, limit: 1, offset: 0 }

    const pingLineItems = await dispatch(dispatchName, { apiParams: pingParams, reportKey }).catch(catcher)
    const { totalCount } = pingLineItems

    return totalCount
  },

  async fetchDiscrepancyData(context, { catcher, apiParams }) {
    return api.getPublisherDiscrepancy(apiParams).catch(catcher)
  },
  fetchHealthAdx(context, { catcher, apiParams }) {
    return api.getHealthAdx(apiParams).catch(catcher)
  },
  fetchHealthNetwork(context, { catcher, apiParams }) {
    return api.getHealthNetwork(apiParams).catch(catcher)
  },
  fetchYieldPartner(context, { catcher, apiParams }) {
    return api.getYieldPartner(apiParams).catch(catcher)
  },
  fetchDashboards() {
    return Promise.resolve({ content: [], totalCount: 0 })
  },

  async fetchDeliveryData({ dispatch }, { apiParams, catcher, reportKey }) {
    const root = { root: true }
    const dispatchMap = {
      dashboard: 'fetchDashboards',
      pacing: 'fetchLineItems',
      discrepancy: 'fetchDiscrepancyData',
      [PATHS.REPORTS.ADX_HEALTH]: 'fetchHealthAdx',
      [PATHS.REPORTS.NETWORK_HEALTH]: 'fetchHealthNetwork',
      [PATHS.REPORTS.YIELD_PARTNER]: 'fetchYieldPartner',
    }
    const fetchFunctionKey = !apiParams.publisherId ? 'dashboard' : reportKey
    const limit = false
    const getTotalCounts = async (dataType) => {
      const dispatchName = dispatchMap[dataType]
      const endpointParams = { apiParams, catcher, dispatchName, reportKey }
      const totalItems = await dispatch('pingEndpoint', endpointParams)
      return Math.ceil(totalItems / limit)
    }

    const countRequestsLineItems = !limit ? 1 : await getTotalCounts(reportKey)
    const loopRequestsLineItems = Array.from({ length: countRequestsLineItems })

    const messageStatus = (response, dataType) => {
      const { totalCount } = response
      dispatch('messages/showLoadingMessage', { reportKey, text: `Receiving ${totalCount} ${dataType} ...` }, root)
      return response
    }
    const responseSeed = Promise.resolve({ responses: [], params: { ...apiParams, limit, offset: 0 } })
    const fetchItems = (dataType, loop) => loop.reduce(async (promiseArray, currentLoop) => {
      const { responses, params } = await promiseArray
      const currentOffset = typeof limit === 'number' && (
        currentLoop || 0
      ) * limit
      const currentParams = { ...params, offset: currentOffset }
      const dispatchName = dispatchMap[dataType]
      const responsePromise = dispatch(dispatchName, { apiParams: currentParams, reportKey }).then((res) => {
        messageStatus(res, dataType)
        return res
      }).catch(catcher)

      responses.push(responsePromise)

      return { responses, params }
    }, responseSeed)

    return fetchItems(fetchFunctionKey, loopRequestsLineItems)
  },

  async transformDeliveryData({ dispatch }, data) {
    const { columnsByKey, publisher, reportKey, response } = data
    const { networkCode } = publisher
    const root = { root: true }

    data.response = response.reduce((responseObject, res) => {
      const { content, error, totalCount } = res
      const isSuccessResponse = Array.isArray(content)
      const foundError = !isSuccessResponse ? error || content : error

      if (isSuccessResponse) responseObject.content = responseObject.content.concat(content)
      if (foundError) responseObject.errors.push(foundError)
      if (responseObject.totalCount !== totalCount) responseObject.totalCount = totalCount

      return responseObject
    }, { content: [], errors: [], totalCount: 0 })

    const foundErrors = !!data.response.errors.length && data.response.errors

    if (foundErrors) data.error = foundErrors.length === 1 ? foundErrors[0] : foundErrors

    const transformMap = {
      discrepancy: () => transform.discrepancy.bind(transform, networkCode, columnsByKey),
      pacing: () => transform.pacing.bind(transform, networkCode, columnsByKey),
      [PATHS.REPORTS.ADX_HEALTH]: () => transform.freezeAll.bind(transform, networkCode, columnsByKey),
      [PATHS.REPORTS.NETWORK_HEALTH]: () => transform.freezeAll.bind(transform, networkCode, columnsByKey),
      [PATHS.REPORTS.YIELD_PARTNER]: () => transform.freezeAll.bind(transform, networkCode, columnsByKey),
    }
    const transformer = !foundErrors && transformMap[reportKey]()

    return dispatch('tools/transformData', { data, transformer }, root)
  },

  setPacingSummaries({ dispatch, state }, itemsReport) {
    const { summaries } = state
    const isAlert = false
    const summariesConfig = {
      isAlert,
      ...itemsReport,
      summaries,
    }

    dispatch('tools/summaries', summariesConfig, { root: true })
  },

  renderDeliveryView({ dispatch }, data) {
    const { params, publisher, reportKey } = data
    const timeLapse = 0

    return new Promise((resolve) => {
      setTimeout(async () => {
        const { networkCode } = publisher

        const lineItemData = { ...data, networkCode }
        const results = await dispatch('transformDeliveryData', lineItemData)
        const { items } = results

        dispatch('messages/showLoadingMessage', { reportKey, text: 'Calculating summaries ...' }, { root: true })

        const itemsReport = { ...params, items, networkCode, reportKey }

        if (reportKey === 'pacing') {
          dispatch('setPacingSummaries', itemsReport)
        }

        dispatch('messages/hideLoadingMessage', { reportKey, text: 'Rendering ...' }, { root: true })

        resolve(results)
      }, timeLapse)
    })
  },

  async setDeliveryView({ dispatch }, { columnsByKey, params, publisher, reportKey }) {
    const { publisherId, reportId, subId, teamId } = params
    const apiParams = { publisherId: publisherId || reportId, teamId: teamId || subId }
    const root = { root: true }

    const endpointName = reportKey
    const moduleName = reportKey
    const applyFilters = true
    const requestReport = { applyFilters, endpointName, moduleName, reportKey }

    const handler = await dispatch('tools/requestHandler', requestReport, root)
    const { catcher } = handler

    dispatch('messages/showLoadingMessage', { reportKey, text: 'Requesting data ...' }, root)

    const reportData = await dispatch('fetchDeliveryData', { apiParams, catcher, reportKey })
    const responses = await reportData.responses
    const response = await Promise.all(responses)

    dispatch('messages/showLoadingMessage', { reportKey, text: 'Transforming data ...' }, root)

    return dispatch('renderDeliveryView', { ...requestReport, columnsByKey, handler, params, publisher, response })
  },

  async getDeliveryData({ dispatch, rootGetters, rootState }, reportKey) {
    const columnsByKey = rootGetters['tools/columnsByKey']
    const publisher = rootState.tools.selectedSearch
    const params = rootState.tools.currentReportParams

    /**
     * This setTimeout allows firing chained and async events before the
     * UI is locked by various mutations.
     */
    const timeLapse = 0

    return new Promise((resolve) => {
      setTimeout(async () => {
        const data = await dispatch('setDeliveryView', { columnsByKey, params, publisher, reportKey })

        return resolve(data)
      }, timeLapse)
    })
  },

  async getDiscrepancyCreatives({ commit }, params) {
    commit(actionNames.GET_DISCREPANCY_CREATIVES_REQUESTED)

    const catcher = (err) => {
      const error = http.catch(err)
      commit(actionNames.GET_DISCREPANCY_CREATIVES_FAILED, error)
      return error
    }

    const creatives = await api.getDiscrepancyCreatives(params).catch(catcher)
    const { content, error } = creatives || {}

    if (error) return catcher(creatives)

    // Filter out null items that may come from backend data inconsistencies
    const validContent = (Array.isArray(content) ? content : []).filter((item) => item != null)
    const items = _.sortBy(validContent, 'date').reverse()

    commit(actionNames.GET_DISCREPANCY_CREATIVES_SUCCEEDED, items)

    return creatives
  },

  get10DayHistoryRequests({ commit }, params) {
    const catcher = (err) => {
      const error = http.catch(err)
      commit(actionNames.GET_10_DAY_HISTORY_FAILED, error)
      return error
    }

    const endDate = dates.now()
    const startDate = endDate.clone().subtract(10, 'days')
    const data = { ...params, startDate, endDate }

    const deliveryRequest = api
      .getDeliveryReportDataForLineItemWithDates(data)
      .catch(catcher)

    const pacingRequest = api
      .getPacingReportDataForLineItemWithDates(data)
      .catch(catcher)

    const requests = [deliveryRequest, pacingRequest]

    return Promise.all(requests)
  },
  async get10DayHistory({ commit, dispatch }, params) {
    commit(actionNames.GET_10_DAY_HISTORY_REQUESTED)

    fireanalytics.event('goal_initialization', {
      category: 'History',
      description: '10 Day History Requested',
      label: `10 Day History Requested for Line Item ${params.lineItemId} in Publisher ${(params.teamId && `${params.publisherId}/${params.teamId}`) || params.publisherId}`,
    })

    const promises = await dispatch('get10DayHistoryRequests', params)
    const data = await promises
    const [deliveryResponse, pacingResponse] = data
    const { content: delivery, error: deliveryError } = deliveryResponse
    const { content: pacing, error: pacingError } = pacingResponse

    // @TODO Combine errors when responding to multiple requests.
    if (deliveryError) return deliveryResponse
    if (pacingError) return pacingResponse

    const raw = [delivery, pacing]
    const results = transform.processLineItemHistoryData(raw)

    commit(actionNames.GET_10_DAY_HISTORY_SUCCEEDED, results)

    fireanalytics.event('goal_completion', {
      category: 'History',
      description: '10 Day History Succeeded',
      label: `10 Day History Succeeded for Line Item ${params.lineItemId} in Publisher ${(params.teamId && `${params.publisherId}/${params.teamId}`) || params.publisherId}`,
    })

    return results
  },

  getCreativeDeliveryByLineItemId({ commit }, params) {
    const catcher = (err) => {
      const error = http.catch(err)
      commit(actionNames.GET_CREATIVES_REPORT_FAILED, error)
      return error
    }

    return api.getCreativesReport(params).catch(catcher)
  },
  async getCreativesReport({ commit, dispatch }, params) {
    commit(actionNames.GET_CREATIVES_REPORT_REQUESTED)

    const catcher = (err) => {
      const error = http.catch(err)
      commit(actionNames.GET_CREATIVES_REPORT_FAILED, error)
      return error
    }

    const reportResponse = await dispatch('getCreativeDeliveryByLineItemId', params)
    const { content: reportCreatives, error: reportError } = reportResponse

    if (reportError) return catcher(reportResponse)

    const groups = reportCreatives.reduce((creativeGroups, creative) => {
      const { creativeCode, creativeName, creativeSize } = creative

      if (!creativeGroups[creativeCode]) {
        creativeGroups[creativeCode] = {
          creativeCode,
          creativeName,
          creativeSize,
          rows: [],
        }
      }

      creativeGroups[creativeCode].rows.push(creative)

      return creativeGroups
    }, {})

    const valueGetter = (row) => dates.get(row.date).unix()
    const items = Object.keys(groups).reduce((creativeGroups, creativeCode) => {
      const thisCreative = groups[creativeCode]
      const { rows } = thisCreative

      creativeGroups[creativeCode] = thisCreative
      creativeGroups[creativeCode].rows = _.sortDscByCaseInsensitive(rows, valueGetter)

      return creativeGroups
    }, {})

    commit(actionNames.GET_CREATIVES_REPORT_SUCCEEDED, items)

    return items
  },
  async getCreatives({ commit }, query) {
    commit(actionNames.GET_CREATIVE_REQUESTED)

    const catcher = (err) => {
      const error = http.catch(err)
      commit(actionNames.GET_CREATIVE_FAILED, error)
      return error
    }

    const { code: lineItemId, networkCode } = query

    const apiParams = { licaIds: [{ lineItemId }], networkCode }
    const licaResponse = await api.getGamLicas(apiParams)
    const { content: licaContent, error: licaError } = licaResponse

    if (licaError) return catcher(licaResponse)

    // Guard against null or empty licaContent
    if (!licaContent || licaContent.length === 0) {
      commit(actionNames.GET_CREATIVE_SUCCEEDED, [])
      return licaResponse
    }

    const ids = licaContent.map((lica) => {
      const { creativeId: id } = lica
      return { id }
    })
    const creativeResponse = await api.postGamCreatives({ ids, networkCode })
    const { content: creatives, error: creativeError } = creativeResponse

    if (creativeError) return catcher(creativeResponse)

    commit(actionNames.GET_CREATIVE_SUCCEEDED, creatives)

    return licaResponse
  },
  async getTearsheet({ commit }, query) {
    commit(actionNames.GET_TEARSHEET_REQUESTED)

    const catcher = (err) => {
      const error = http.catch(err)
      commit(actionNames.GET_TEARSHEET_FAILED, error)
      return error
    }

    const licaResponse = await api.postTearsheet(query).catch(catcher)
    const { content: tearsheetUrl, error } = licaResponse

    if (error) return catcher(licaResponse)

    const { creativeId } = query

    commit(actionNames.GET_TEARSHEET_SUCCEEDED, { creativeId, tearsheetUrl })

    return licaResponse
  },

  showLineItemHistoryModal({ commit, dispatch }, item) {
    commit('currentRecord', item)
    commit('isLineItemHistoryModalVisible', true)

    dispatch('get10DayHistory', item)
  },
  showCreativesReportModal({ commit, dispatch }, item) {
    commit('currentRecord', item)
    commit('isCreativesReportModalVisible', true)

    dispatch('getCreativesReport', item)
  },
  showTearsheetModal({ commit, dispatch }, item) {
    commit('currentRecord', item)
    commit('isCreativeModalVisible', true)

    dispatch('getCreatives', item)
  },
  showCreativesDiscrepancyModal({ commit, dispatch }, item) {
    commit('currentRecord', item)
    commit('isDiscrepancyCreativeModalVisible', true)
    dispatch('getDiscrepancyCreatives', item)
  },
}

export default actions
